import { BillOfMaterialsModel } from '~/pages/BillOfMaterials/models/BillOfMaterialsModel';
import { ReservoirModel } from '~/shared/models/system/ReservoirModel';
import { SystemModel } from '~/shared/models/system/SystemModel';

const BOM_PARAMETERS_STORAGE_KEY = 'bomFormValues';

export type BOMParameters = {
  screwInserts: boolean;
  connectorToolAquacell160: boolean;
};

export class CreateBomParametersModel {
  static readonly CUTTING_LOSS_PERCENT = 10;

  private static readonly defaultFormValues: BOMParameters = {
    screwInserts: false,
    connectorToolAquacell160: false,
  };

  static getDefault(system?: SystemModel): BOMParameters {
    const storedValues = sessionStorage.getItem(BOM_PARAMETERS_STORAGE_KEY);

    if (storedValues) {
      return CreateBomParametersModel.parseStored(storedValues);
    }

    return {
      ...CreateBomParametersModel.defaultFormValues,
      connectorToolAquacell160: system
        ? CreateBomParametersModel.systemHasAquacell160(system)
        : false,
    };
  }

  static save(values: BOMParameters) {
    sessionStorage.setItem(BOM_PARAMETERS_STORAGE_KEY, JSON.stringify(values));
  }

  static toGetBomRequest(systemName: string, bomParameters: BOMParameters) {
    return {
      systemName,
      cuttingLoss: CreateBomParametersModel.CUTTING_LOSS_PERCENT,
      ...bomParameters,
    };
  }

  static systemHasAquacell160(system: SystemModel): boolean {
    return system.reservoirs.some(ReservoirModel.usesAquacell160Crate);
  }

  static billOfMaterialsHasAquacell160(
    billOfMaterials: BillOfMaterialsModel,
  ): boolean {
    const lineItemsReferenceAquacell160 = billOfMaterials.items.some(
      ({ items }) =>
        items.some(
          (item) =>
            CreateBomParametersModel.textReferencesAquacell160(item.title) ||
            CreateBomParametersModel.textReferencesAquacell160(
              item.description,
            ),
        ),
    );

    const productsUsedReferenceAquacell160 = billOfMaterials.productsUsed.some(
      (product) =>
        CreateBomParametersModel.textReferencesAquacell160(product.title),
    );

    return lineItemsReferenceAquacell160 || productsUsedReferenceAquacell160;
  }

  static projectHasAquacell160(
    system: SystemModel,
    billOfMaterials?: BillOfMaterialsModel,
  ): boolean {
    return (
      CreateBomParametersModel.systemHasAquacell160(system) ||
      (billOfMaterials
        ? CreateBomParametersModel.billOfMaterialsHasAquacell160(
            billOfMaterials,
          )
        : false)
    );
  }

  static enableConnectorToolIfNeeded(
    parameters: BOMParameters,
    system: SystemModel,
    billOfMaterials?: BillOfMaterialsModel,
  ): BOMParameters | null {
    if (parameters.connectorToolAquacell160) {
      return null;
    }

    if (
      !CreateBomParametersModel.projectHasAquacell160(system, billOfMaterials)
    ) {
      return null;
    }

    return {
      ...parameters,
      connectorToolAquacell160: true,
    };
  }

  private static parseStored(storedValues: string): BOMParameters {
    const parsed = JSON.parse(storedValues) as Partial<BOMParameters>;

    return {
      screwInserts:
        parsed.screwInserts ??
        CreateBomParametersModel.defaultFormValues.screwInserts,
      connectorToolAquacell160:
        parsed.connectorToolAquacell160 ??
        CreateBomParametersModel.defaultFormValues.connectorToolAquacell160,
    };
  }

  private static textReferencesAquacell160(text: string): boolean {
    return /\bAquaCell\s+160\b/i.test(text);
  }
}
